import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DigisubservicesService } from '../services/digisubservices.service';

interface IssueStructure {
  month: string;
  year_suffix: string;
  issues: string[];
  colspan: number;
}

interface RepresentativeSummary {
  off_rep_name: string;
  categorywise: number;
  data: any[];
  total: number;             // Current Year Business
  rcv_total: number;         // Total Received Amount (rcv_amt)
  donation_amt: number;      // देणगी/अन्य amount
  prev_year_billing: number; // Mapping from PHP field
  target: number;            // For Input
  currentPercentage: number; // For Column 5
  old_outstanding: number;
  current_period_unpaid: number;
  total_unpaid: number;
}

@Component({
  selector: 'app-business-report-very-briefly',
  templateUrl: './business-report-very-briefly.component.html',
  styleUrl: './business-report-very-briefly.component.scss'
})
export class BusinessReportVeryBrieflyComponent {


  rowColors = [
  '#FFE0E0', // light red
  '#c9e0f5', // light blue
  '#f5d9c9', // light green
  '#f5e4be', // light orange
  '#d2bae9', // light purple
  '#c2fafa', // light cyan
  '#e6e6a1', // light yellow
];

getRowColor(index: number): string {
  return this.rowColors[index % this.rowColors.length];
}

  businessReportData: any[] = [];
  isLoading: boolean = true;
  headerStructure: IssueStructure[] = [];
  representativeSummaries: RepresentativeSummary[] = [];
  financialYearDisplay: string = '';
  financialYearLabel: string = '';
  selectedYearRange: string = '';

  // New properties for dynamic headers
  oldOutstandingHeader: string = '';
  currentPeriodHeader: string = '';

  constructor(
    private dialogRef: MatDialogRef<BusinessReportVeryBrieflyComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private service: DigisubservicesService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Get the year range in format "25-26"
    this.selectedYearRange = this.getYearRangeFromDate(this.data['startDate']);

    // Set dynamic headers
    this.setDynamicHeaders(this.data['startDate']);

    const payload = {
      start_date: this.data['startDate'],
      end_date: this.data['endDate'],
      financial_year: this.data['financial_year'],
      rep_name: this.data['repname'] || '',
      off_rep_name: this.data['offRepName'] || '',
      district: this.data['district'],
      taluka: this.data['taluka']
    };
    this.loadBusinessReport(payload);
  }

  // Updated method to set dynamic headers based on selected date
  setDynamicHeaders(dateString: string): void {
    if (!dateString) {
      this.oldOutstandingHeader = 'एकूण १३ ते २५';
      this.currentPeriodHeader = '२५-२६';
      return;
    }

    try {
      const date = new Date(dateString);
      const currentYear = date.getFullYear();

      // Get last two digits
      const currentYearShort = currentYear.toString().slice(-2);
      const nextYearShort = (currentYear + 1).toString().slice(-2);

      // Format: "एकूण १३ ते २५" - use CURRENT year from start_date
      this.oldOutstandingHeader = `एकूण १३ ते ${currentYearShort}`;

      // Format: "२५-२६" becomes "{current year}-{next year}"
      this.currentPeriodHeader = `${currentYearShort}-${nextYearShort}`;

    } catch (e) {
      // Fallback to default
      this.oldOutstandingHeader = 'एकूण १३ ते २५';
      this.currentPeriodHeader = '२५-२६';
    }
  }

  // Get year range in "25-26" format
  getYearRangeFromDate(dateString: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const currentYear = date.getFullYear();

      // Extract last two digits of current year and next year
      const currentYearShort = currentYear.toString().slice(-2);
      const nextYearShort = (currentYear + 1).toString().slice(-2);

      return `${currentYearShort}-${nextYearShort}`;
    } catch (e) {
      // Fallback: try to extract year from string
      const yearMatch = dateString.match(/\d{4}/);
      if (yearMatch) {
        const currentYear = parseInt(yearMatch[0]);
        const currentYearShort = currentYear.toString().slice(-2);
        const nextYearShort = (currentYear + 1).toString().slice(-2);
        return `${currentYearShort}-${nextYearShort}`;
      }
      return '';
    }
  }

  loadBusinessReport(payload: any): void {
    this.isLoading = true;

    this.service.getBusinessReportVBriefly(payload).subscribe(
      (response: any) => {
        console.log('API RESPONSE:', response);

        // ✅ YOUR API RETURNS ARRAY DIRECTLY
        this.businessReportData = Array.isArray(response) ? response : [];

        // If financialYearLabel is not available from API, use the year range
        this.financialYearLabel = response.financial_year_label || this.selectedYearRange || '';

        this.processRepresentativeData();

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error loading business report:', error);
        this.isLoading = false;
      }
    );
  }

  processRepresentativeData(): void {
    if (!this.businessReportData || this.businessReportData.length === 0) {
      this.representativeSummaries = [];
      return;
    }

    this.representativeSummaries = this.businessReportData.map((item: any) => ({
      off_rep_name: item.off_rep_name,
      categorywise: 999,
      data: [],
      total: Number(item.amount) || 0,
      rcv_total: Number(item.rcv_amt) || 0,
      donation_amt: Number(item.donation_amt) || 0,
      old_outstanding: Number(item.old_outstanding) || 0,
      current_period_unpaid: Number(item.current_period_unpaid) || 0,
      prev_year_billing: 0,
      target: Number(item.udhistha) || 0,
      currentPercentage: 0,
      unpaid_amt: Number(item.unpaid_amt) || 0,
      total_unpaid: Number(item.total_unpaid) || 0,
    }));

    console.log('REP SUMMARY:', this.representativeSummaries);
  }

  calculateMetrics(rep: any) {
    if (rep.target > 0) {
      rep.currentPercentage = parseFloat(((rep.total / rep.target) * 100).toFixed(2));
    } else {
      rep.currentPercentage = 0;
    }
  }

  getOverallPercentage(): string {
  const target = this.getTotalTarget();
  if (!target || target === 0) return '-';
  return Math.round((this.calculateGrandTotal() / target) * 100) + '%';
}

  calculateTotalPercentage(rep: RepresentativeSummary): number {
    const totalBusiness = rep.total;
    if (rep.target && rep.target > 0) {
      return Math.round((totalBusiness / rep.target) * 100);
    }
    return 0;
  }

  // एकूण जमा for a single rep = जाहिरात + देणगी/अन्य
  getEkunJama(rep: RepresentativeSummary): number {
    return (rep.rcv_total || 0) + (rep.donation_amt || 0);
  }

  getTotalTarget(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + (rep.target || 0), 0);
  }

  getTotalPrevYearBilling(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + rep.prev_year_billing, 0);
  }

  calculateGrandTotal(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + rep.total, 0);
  }

  getTotalRcvAmount(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + rep.rcv_total, 0);
  }

  getTotalDonationAmount(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + (rep.donation_amt || 0), 0);
  }

  getTotalEkunJama(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + this.getEkunJama(rep), 0);
  }

  getTotalUnpaidAmount(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + rep.old_outstanding, 0);
  }

  getCTotalUnpaidAmount(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + rep.current_period_unpaid, 0);
  }

  getGTotalUnpaidAmount(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + rep.total_unpaid, 0);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

printPageA3Portrait(): void {
  const printSection = document.getElementById('business-report-table');
  if (!printSection) return;

  // ✅ Capture live input values before cloning (outerHTML can't see Angular ngModel values)
  const originalInputs = Array.from(printSection.querySelectorAll('input'));
  const inputValues = originalInputs.map((inp: any) => inp.value || '0');

  const cloned = printSection.cloneNode(true) as HTMLElement;
  const clonedInputs = Array.from(cloned.querySelectorAll('input'));
  clonedInputs.forEach((inp: any, idx: number) => {
    const span = document.createElement('span');
    span.textContent = inputValues[idx] ?? '0';
    inp.parentNode?.replaceChild(span, inp);
  });

  const printWindow = window.open('', '', 'width=1400,height=900');
  if (!printWindow) return;

  printWindow.document.write(`
    <html>
      <head>
        <title>व्यवसाय अहवाल</title>
        <style>
          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          @page { size: A4 landscape; margin: 6mm; }
          html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; background: #fff; }
          #print-wrapper { transform-origin: top left; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #000; padding: 12px; font-size: 24px; text-align: center; }
          .amount-cell { text-align: right; }
          .total-row, tfoot tr { font-weight: bold; background: #f0f0f0 !important; }
          input { border: none !important; background: transparent !important; }
        </style>
      </head>
      <body>
        <div id="print-wrapper">${cloned.outerHTML}</div>
        <script>
          window.onload = function () {
            var wrapper = document.getElementById('print-wrapper');
            var table = wrapper.querySelector('table');
            if (table) {
              var PAGE_WIDTH_PX = 1080; // usable A4 landscape width after 6mm margins, 96dpi
              var naturalWidth = table.scrollWidth;
              var scale = PAGE_WIDTH_PX / naturalWidth;
              wrapper.style.transform = 'scale(' + scale + ')';
              wrapper.style.width = (100 / scale) + '%';
            }
            setTimeout(function () {
              window.print();
              window.close();
            }, 400);
          };
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
}

formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';

  return Math.trunc(value).toString();
}
}